import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api.ts';
import { Thread } from '../../components/Thread.tsx';
import { relativeTime } from '../../messages.ts';

interface SupportThread { id: string; name: string; email: string; role: string; lastMessage: string; lastSide: string; lastMessageAt: string; unread: number }
interface Report { id: string; reason: string; createdAt: string; missionTitle: string | null; reporter: string; sender: string; senderSide: string; body: string; masked: boolean }
interface ContextMessage { id: string; side: string; author: string; body: string; masked: boolean; urgent: boolean; createdAt: string; reported: boolean }
interface ContactAlert { id: string; userId: string; name: string; role: string | null; organization: string | null; maskedThisWeek: number; createdAt: string }
interface MaskedMessage { id: string; mission: string | null; shown: string; original: string; createdAt: string }

const ROLES: Record<string, string> = { EXTRA: 'Extra', ORG_OWNER: 'Restaurateur', ORG_MANAGER: 'Restaurateur' };

/** Back-office de la messagerie : fil avec le support, signalements, tentatives de partage de coordonnées. */
export function AdminMessaging() {
  const [threads, setThreads] = useState<SupportThread[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [reports, setReports] = useState<Report[] | null>(null);
  const [context, setContext] = useState<{ id: string; messages: ContextMessage[] } | null>(null);
  const [alerts, setAlerts] = useState<ContactAlert[] | null>(null);
  const [masked, setMasked] = useState<{ name: string; messages: MaskedMessage[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ threads: SupportThread[] }>('/admin/messaging/support').then((r) => setThreads(r.threads));
    api.get<{ reports: Report[] }>('/admin/messaging/reports').then((r) => setReports(r.reports));
    api.get<{ alerts: ContactAlert[] }>('/admin/messaging/alerts').then((r) => setAlerts(r.alerts));
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(() => document.visibilityState === 'visible' && load(), 20_000);
    return () => clearInterval(timer);
  }, [load]);

  async function act(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Action impossible.');
    }
  }

  const resolve = (url: string, label: string) => {
    const note = window.prompt(label);
    if (note?.trim()) void act(() => api.post(url, { note }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {error && <div className="error-banner">{error}</div>}

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Messages au support ({threads?.filter((t) => t.unread > 0).length ?? 0} à traiter)</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Les extras et établissements écrivent ici. Le support est la plateforme : les coordonnées ne sont pas masquées dans ces échanges.
        </div>
        {threads && threads.length === 0 && <div className="empty-state">Aucun message pour l’instant.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 340px) 1fr', gap: 14, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {threads?.map((t) => (
              <button key={t.id} type="button" className="card" onClick={() => setSelected(t.id)}
                style={{ textAlign: 'left', cursor: 'pointer', borderColor: selected === t.id ? 'var(--accent)' : undefined, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>{t.name}</strong>
                  {t.unread > 0 && <span className="badge" style={{ background: 'var(--accent)', color: '#fff' }}>{t.unread}</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{ROLES[t.role] ?? t.role} · {t.email} · {relativeTime(t.lastMessageAt)}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.lastSide === 'SUPPORT' ? 'Vous : ' : ''}{t.lastMessage}</div>
              </button>
            ))}
          </div>
          <div className="card">{selected ? <Thread key={selected} conversationId={selected} embedded /> : <div className="empty-state">Choisissez une conversation.</div>}</div>
        </div>
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Messages signalés ({reports?.length ?? 0})</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Le texte affiché est celui que voit l’autre partie (coordonnées masquées). « Voir le contexte » montre l’original : chaque lecture est journalisée.
        </div>
        {reports && reports.length === 0 && <div className="empty-state">Aucun signalement.</div>}
        {reports?.map((r) => (
          <div key={r.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            <div style={{ fontSize: 13 }}><strong>{r.reporter}</strong> signale un message de <strong>{r.sender}</strong>{r.missionTitle && <> · {r.missionTitle}</>} · {relativeTime(r.createdAt)}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Motif : {r.reason}</div>
            <div style={{ fontSize: 13.5, background: 'var(--bg)', borderRadius: 8, padding: '8px 10px', whiteSpace: 'pre-wrap' }}>{r.body}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => act(async () => { const res = await api.get<{ messages: ContextMessage[] }>(`/admin/messaging/reports/${r.id}/context`); setContext({ id: r.id, messages: res.messages }); })}>Voir le contexte</button>
              <button className="btn btn-primary btn-sm" onClick={() => resolve(`/admin/messaging/reports/${r.id}/resolve`, 'Décision (visible dans le journal) :')}>Clore</button>
            </div>
            {context?.id === r.id && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {context.messages.map((m) => (
                  <div key={m.id} style={{ fontSize: 13, background: m.reported ? 'var(--danger-tint)' : undefined, borderRadius: 6, padding: '2px 6px' }}>
                    <strong>{m.author}</strong> <span style={{ color: 'var(--text-tertiary)' }}>({m.side === 'EXTRA' ? 'extra' : 'établissement'}, {new Date(m.createdAt).toLocaleString('fr-FR')})</span> : {m.body}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Partage de coordonnées ({alerts?.length ?? 0})</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Trois tentatives de partager un numéro, un e-mail ou un lien en une semaine. Aucune sanction automatique : à vous de décider (rappel des règles, avertissement…).
        </div>
        {alerts && alerts.length === 0 && <div className="empty-state">Aucune alerte.</div>}
        {alerts && alerts.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Utilisateur</th><th>Tentatives (7 j)</th><th>Depuis</th><th /></tr></thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id}>
                    <td><strong>{a.name}</strong><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{ROLES[a.role ?? ''] ?? a.role}{a.organization && ` · ${a.organization}`}</div></td>
                    <td>{a.maskedThisWeek}</td>
                    <td>{relativeTime(a.createdAt)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => act(async () => { const res = await api.get<{ messages: MaskedMessage[] }>(`/admin/messaging/masked/${a.userId}`); setMasked({ name: a.name, messages: res.messages }); })}>Voir les messages</button>{' '}
                      <button className="btn btn-primary btn-sm" onClick={() => resolve(`/admin/messaging/alerts/${a.id}/resolve`, 'Décision (visible dans le journal) :')}>Clore</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {masked && (
          <div className="card" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong>Messages masqués de {masked.name}</strong>
              <button className="btn btn-ghost btn-sm" onClick={() => setMasked(null)}>Fermer</button>
            </div>
            {masked.messages.map((m) => (
              <div key={m.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 8, fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{new Date(m.createdAt).toLocaleString('fr-FR')}{m.mission && ` · ${m.mission}`}</div>
                <div>Original : <strong>{m.original}</strong></div>
                <div style={{ color: 'var(--text-secondary)' }}>Vu par l’autre partie : {m.shown}</div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
